import {
  AdvanceTitledListTile,
  AnalyticsEvent,
  DmgmScreenTilesResult,
  ITile,
  TileServiceTile,
  TileStatus,
  Typenames,
} from '@varo/graphql-schema-types';
import { ImperativeRouter, useRouter } from 'expo-router';
import { advancedSectionTile } from 'features/borrowingPower/BorrowingPowerScreen/adapters/advancedSectionTile';
import { pageSectionTile } from 'features/borrowingPower/BorrowingPowerScreen/adapters/pageSectionTile';
import {
  BorrowingPowerDependencies,
  BorrowingPowerScreenCallbacks,
} from 'features/borrowingPower/BorrowingPowerScreen/adapters/types';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { SegmentInterface } from 'services/analytics/segment/types';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import useDefaultError from 'shared/hooks/errorFormatters/useDefaultError/useDefaultError';
import {
  genericRouteAction,
  UnknownGQLType,
} from 'shared/legacySDUIAdapters/genericRouteAction';
import { VXLNode } from 'shared/renderer/types';
import { CoreError } from 'shared/utils/errors/CoreError';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';
import { Maybe } from 'shared/utils/types';

export interface BorrowingPowerScreenContent {
  displayAnalytics?: Maybe<AnalyticsEvent>;
  sections: VXLNode[][];
}

const navigateForActionWithRouter = (
  action: Maybe<UnknownGQLType>,
  router: ImperativeRouter,
  dismissBottomSheet: () => void,
  segmentClient: SegmentInterface,
) => {
  if (!action) {
    return;
  }

  dismissBottomSheet();

  const routeAction = genericRouteAction(action);
  if (routeAction) {
    const { pathname, params, analyticsEvent } = routeAction;
    if (analyticsEvent) {
      const { name, properties } = parseGQLAnalyticsEvent(analyticsEvent);
      void segmentClient.logEvent(name, properties);
    }

    router.dismissTo({
      pathname,
      params,
    });
  }
};

const navigateForAction =
  (
    router: ImperativeRouter,
    dismissBottomSheet: () => void,
    segmentClient: SegmentInterface,
  ) =>
  (action: Maybe<UnknownGQLType>) =>
    navigateForActionWithRouter(
      action,
      router,
      dismissBottomSheet,
      segmentClient,
    );

const mapBorrowingPowerTiles = (
  tiles: ITile[],
  dependencies: BorrowingPowerDependencies,
): VXLNode[][] => {
  return tiles
    .map((tile: ITile) => {
      if (tile.status !== TileStatus.Loaded || !('__typename' in tile)) {
        return null;
      }

      if (tile.__typename === Typenames.TileServiceTile) {
        const tileServiceTile = tile as TileServiceTile;
        if (
          !tileServiceTile.container ||
          tileServiceTile.container?.__typename !==
            Typenames.VXLPageSectionVerticalStackContainer
        ) {
          return null;
        }

        return pageSectionTile(tileServiceTile.container, dependencies);
      } else if (tile.__typename === Typenames.AdvanceTitledListTile) {
        const advancedTitledListTile = tile as AdvanceTitledListTile;
        return advancedSectionTile(
          advancedTitledListTile.vxlTitle,
          advancedTitledListTile.tiles,
          dependencies,
        );
      }
    })
    .filter(isNotNil);
};

const useFormatBorrowingPowerData = (
  data: Maybe<DmgmScreenTilesResult>,
  { showBottomSheet, dismissBottomSheet }: BorrowingPowerScreenCallbacks,
): {
  content?: Maybe<BorrowingPowerScreenContent>;
  error?: Maybe<CoreError>;
} => {
  const error = useDefaultError();
  const router = useRouter();
  const { segmentClient } = useAnalyticsConfiguration();

  if (!data) {
    return {};
  }

  if (data.__typename === Typenames.DMGMScreenTilesError) {
    return {
      error: {
        title: data.title,
        message: data.message,
        code: data.code,
      },
    };
  } else if (
    data.__typename === Typenames.GroupedContentSectionScreenTile &&
    data.status === TileStatus.Loaded
  ) {
    const { displayAnalytics, tiles } = data;
    return {
      content: {
        displayAnalytics,
        sections: mapBorrowingPowerTiles(tiles, {
          showBottomSheet,
          navigateForAction: navigateForAction(
            router,
            dismissBottomSheet,
            segmentClient,
          ),
          segmentClient,
        }),
      },
    };
  }
  return { error };
};

export default useFormatBorrowingPowerData;
